import { useCallback, useEffect, useState } from 'react'
import { fetchOutreachOverview, fetchStats } from '../../../lib/outreach/api'
import type { OutreachOverview, WorkspaceStats } from '../../../lib/outreach/types'
import {
  fetchMyWorkspaces,
  getActiveWorkspaceId,
  type WorkspaceSummary,
} from '../../../lib/workspace'

export type Scope = 'business' | 'all'
export const RANGE_OPTIONS = [7, 30, 90] as const
export type RangeDays = (typeof RANGE_OPTIONS)[number]

export type DashData =
  | { scope: 'business'; stats: WorkspaceStats }
  | { scope: 'all'; overview: OutreachOverview }

export interface DashState {
  status: 'loading' | 'ready' | 'error'
  /** Last good payload. Kept while a refresh runs so the page does not flash empty. */
  data: DashData | null
  error: string | null
}

const DAY_MS = 24 * 60 * 60 * 1000

async function load(scope: Scope, days: RangeDays): Promise<DashData> {
  const from = new Date(Date.now() - days * DAY_MS)
  if (scope === 'all') return { scope, overview: await fetchOutreachOverview({ from }) }
  return { scope, stats: await fetchStats({ from }) }
}

export function useOutreachDashboard() {
  const [scope, setScope] = useState<Scope>('business')
  const [days, setDays] = useState<RangeDays>(30)
  const [nonce, setNonce] = useState(0)
  const [state, setState] = useState<DashState>({ status: 'loading', data: null, error: null })
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])

  useEffect(() => {
    let alive = true
    fetchMyWorkspaces().then(
      (rows) => {
        if (alive) setWorkspaces(rows)
      },
      () => {
        // The scope switch just stays hidden when the list cannot load.
      },
    )
    return () => {
      alive = false
    }
  }, [])

  useEffect(() => {
    // Each run cancels the one before it, so a slow older response cannot overwrite a newer one.
    let stale = false
    setState((prev) => ({
      status: 'loading',
      data: prev.data && prev.data.scope === scope ? prev.data : null,
      error: null,
    }))
    load(scope, days).then(
      (data) => {
        if (!stale) setState({ status: 'ready', data, error: null })
      },
      (err: unknown) => {
        if (stale) return
        const message = err instanceof Error && err.message ? err.message : 'Could not load stats.'
        setState({ status: 'error', data: null, error: message })
      },
    )
    return () => {
      stale = true
    }
  }, [scope, days, nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])
  const current = workspaces.find((w) => w.is_current)
  const currentId = current ? current.id : getActiveWorkspaceId()

  return {
    scope,
    setScope,
    days,
    setDays,
    refresh,
    state,
    canPickScope: workspaces.length > 1,
    currentId,
  }
}
